import { useMemo, useState } from 'react';

import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { Checkbox } from '@components/checkbox';
import { hideModal, Modal } from '@components/modal';
import { SearchBox } from '@components/search-box';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldType } from '@components/text-editors/addons/field-mapping';
import {
  FIELD_MAPPING_ICON_COLOR,
  fieldTypeIconMap,
} from '@components/text-editors/addons/field-mapping-type-maps';
import { useScopedTranslate } from '@locale/translate';

import { draftKey } from './serialize';
import { JourneyAttribute, JourneyAttributeType } from './types';

/** Journey attribute types → the FieldType keys of fieldTypeIconMap. */
export const FIELD_TYPE_BY_ATTRIBUTE_TYPE: Record<JourneyAttributeType, FieldType> = {
  text: 'text',
  paragraph: 'paragraph',
  date: 'date',
  number: 'number',
  decimal: 'decimal',
  boolean: 'boolean',
  single_select: 'select',
  multi_select: 'multi_select',
  email: 'email',
  phone_number: 'phone',
  file: 'file',
  file_multi: 'file_multi',
  user: 'user',
};

type AddFieldsModalProps = {
  availableFields: JourneyAttribute[];
  onAdd: (shortIds: string[]) => void;
};

/**
 * Modal for adding journey attributes to the config panel. Any attribute is
 * addable — journey-required and optional alike (the Journeys API does not
 * enforce required attributes). Only shows attributes that are not currently
 * included in the draft, in journey order (the order of availableFields —
 * never re-sorted).
 */
export const AddFieldsModal = ({ availableFields, onAdd }: AddFieldsModalProps) => {
  const { t } = useScopedTranslate(locale);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  const visibleFields = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (query === '') {
      return availableFields;
    }
    return availableFields.filter((field) => field.title.toLowerCase().includes(query));
  }, [availableFields, searchQuery]);

  const areAllVisibleSelected =
    visibleFields.length > 0 && visibleFields.every((field) => selected.has(draftKey(field)));

  const handleToggle = (shortId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(shortId)) {
        next.delete(shortId);
      } else {
        next.add(shortId);
      }
      return next;
    });
  };

  const handleToggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (areAllVisibleSelected) {
        visibleFields.forEach((field) => next.delete(draftKey(field)));
      } else {
        visibleFields.forEach((field) => next.add(draftKey(field)));
      }
      return next;
    });
  };

  const handleConfirm = () => {
    // Emit in journey attribute order, not selection order
    onAdd(
      availableFields
        .filter((field) => selected.has(draftKey(field)))
        .map((field) => draftKey(field))
    );
    hideModal();
  };

  return (
    <Modal>
      <Modal.Header>
        <Modal.HeaderTitle title={t('title')} />
      </Modal.Header>
      <Modal.Content>
        <Stack space={4}>
          {availableFields.length === 0 ? (
            <Text font="regular14" color="textHelper" text={t('noFields')} />
          ) : (
            <Stack space={3}>
              <SearchBox
                dataTestid="add-fields-search"
                placeholder={t('searchPlaceholder')}
                value={searchQuery}
                onChange={setSearchQuery}
              />
              {visibleFields.length === 0 ? (
                <Text font="regular14" color="textHelper" text={t('noSearchResults')} />
              ) : (
                <Stack space={2}>
                  <Checkbox
                    label={t('selectAll')}
                    value={areAllVisibleSelected}
                    onChange={handleToggleAll}
                  />
                  <Separator />
                  {visibleFields.map((field) => {
                    const TypeIcon =
                      fieldTypeIconMap[FIELD_TYPE_BY_ATTRIBUTE_TYPE[field.attributeType] ?? 'text'];
                    return (
                      <Checkbox
                        key={draftKey(field)}
                        label={field.title}
                        icon={<TypeIcon color={FIELD_MAPPING_ICON_COLOR} />}
                        value={selected.has(draftKey(field))}
                        onChange={() => handleToggle(draftKey(field))}
                      />
                    );
                  })}
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <ButtonGroup align="right">
          <Button variant="secondary" text={t('cancel')} onClick={hideModal} />
          <Button
            variant="primary-blue"
            text={t(selected.size === 1 ? 'addFieldOne' : 'addFieldsMany', {
              count: selected.size,
            })}
            onClick={handleConfirm}
            disabled={selected.size === 0}
          />
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

const locale = {
  en: {
    title: 'Add fields',
    noFields: 'All fields from this journey have been added',
    noSearchResults: 'No fields match your search',
    searchPlaceholder: 'Search fields...',
    selectAll: 'Select all',
    cancel: 'Cancel',
    addFieldOne: 'Add 1 field',
    addFieldsMany: 'Add {count} fields',
  },
  is: {
    title: 'Bæta við reitum',
    noFields: 'Öllum reitum úr þessu Journey-ferli hefur verið bætt við',
    noSearchResults: 'Engir reitir fundust við leitina',
    searchPlaceholder: 'Leita að reitum…',
    selectAll: 'Velja alla',
    cancel: 'Hætta við',
    addFieldOne: 'Bæta við 1 reit',
    addFieldsMany: 'Bæta við {count} reitum',
  },
};
